import { maxBy, minBy } from 'es-toolkit';

import { BevelCutTypePreference, TrussEnvelopeBevelCut, TrussEnvelopeBevelCutType } from '@paragon/contract-library';
import {
  angleLessThanOrEqual,
  convertRadiansToDegrees,
  distanceEquals,
  LineSegment2D,
  Point2D,
  point2DEquals,
  Vector2D,
} from '@paragon/geometry-library';
import { getEnvelopeSegmentFromDefiningSegment, TrussEnvelopeThicknessProperties } from '@paragon/layout';
import { max, notNullish, PartialPartial, ReadonlyRecord } from '@paragon/utilities';

import { TrussEnvelopeData } from '../../ecosystem/TrussEnvelopeData';
import { EnvelopeCalculationResult } from './envelopeCalculationResult';
import { StageTwoTrussEnvelope } from './stage2';

export class StageThreeTrussEnvelope {
  /**
   * The previous stage; kept here for access to the ecosystem and to reference when debugging.
   */
  source: StageTwoTrussEnvelope;

  /**
   * The segment that is being proposed as the "correct" truss envelope segment. Copied from stage two because it is
   *   further adjusted here.
   */
  segment: LineSegment2D;

  /**
   * Bevel cuts applied at the base of the truss envelope segment.
   */
  baseBevels: TrussEnvelopeBevelCut | null;

  /**
   * Bevel cuts applied at the end of the truss envelope segment.
   */
  endBevels: TrussEnvelopeBevelCut | null;

  /**
   * Creates a stage three truss envelope and performs the relevant adjustments.
   */
  private constructor(
    source: StageTwoTrussEnvelope,
    segment: LineSegment2D,
    baseBevels: TrussEnvelopeBevelCut | null,
    endBevels: TrussEnvelopeBevelCut | null,
  ) {
    this.source = source;
    this.segment = segment;
    this.baseBevels = baseBevels;
    this.endBevels = endBevels;
  }

  public static fromStageTwoEnvelope(
    source: StageTwoTrussEnvelope,
    bevelCuts: StageThreeEnvelopeBevelCuts | undefined,
    bevelCutTypePreference: BevelCutTypePreference,
  ) {
    const adjusted = StageThreeTrussEnvelope.calculateBevelsAndAdjustSegmentAccordingly(
      source,
      bevelCuts,
      bevelCutTypePreference,
    );

    return adjusted
      ? new StageThreeTrussEnvelope(source, adjusted.segment, adjusted.baseBevels, adjusted.endBevels)
      : null;
  }

  private static calculateBevelsAndAdjustSegmentAccordingly(
    source: StageTwoTrussEnvelope,
    bevelCuts: StageThreeEnvelopeBevelCuts | undefined,
    bevelCutTypePreference: BevelCutTypePreference,
  ) {
    const { segment } = source;

    const bevelCalculator = new BevelCalculator(segment, source.envelopeData, source.boundingTrussEnvelopes);

    const [newBasePoint, baseBevels] = bevelCalculator.calculateBevelAndAdjustedPoint(segment.basePoint, {
      bevelCut: bevelCuts?.baseBevelCut,
      otherTip: segment.endPoint,
      bevelCutTypePreference,
      overhang: source.leftOverhang,
    });

    const [newEndPoint, endBevels] = bevelCalculator.calculateBevelAndAdjustedPoint(segment.endPoint, {
      bevelCut: bevelCuts?.endBevelCut,
      otherTip: segment.basePoint,
      bevelCutTypePreference,
      overhang: source.rightOverhang,
    });

    const basePoint = newBasePoint ?? segment.basePoint;
    const endPoint = newEndPoint ?? segment.endPoint;
    const newSegment = point2DEquals(basePoint, endPoint) ? null : new LineSegment2D(basePoint, endPoint);

    return newSegment && { baseBevels, endBevels, segment: newSegment };
  }

  getResult(): EnvelopeCalculationResult {
    return new EnvelopeCalculationResult(
      this.segment.basePoint.toObject(),
      this.segment.endPoint.toObject(),
      this.source.elevation,
      this.baseBevels,
      this.endBevels,
      this.source.leftOverhang,
      this.source.rightOverhang,
      {
        history: {
          stageOne: this.source.source,
          stageTwo: this.source,
          stageThree: this,
        },
      },
    );
  }
}

/**
 * A bevel cut for a truss envelope as specified by the user.
 * - `null` means no bevel cut.
 * - non-`null` with `angle: undefined` means the specified type of cut with an automatically-calculated angle.
 * - non-`null` with non-`undefined` `angle` means the exact bevel cut specified.
 */
export type UserSpecifiedBevelCut = PartialPartial<TrussEnvelopeBevelCut, 'angle'> | null;

export type StageThreeEnvelopeBevelCuts = {
  baseBevelCut: UserSpecifiedBevelCut | undefined;
  endBevelCut: UserSpecifiedBevelCut | undefined;
};

const doesBevelCutTypeRespectPreference = (preference: BevelCutTypePreference, bevel: TrussEnvelopeBevelCutType) => {
  switch (preference) {
    case 'PreferAny':
      return true;
    case 'PreferDouble':
      return bevel === 'Double';
    case 'PreferNone':
      return false;
  }
};

class BevelCalculator {
  private readonly definingSegment: LineSegment2D;
  private readonly thicknessProperties: TrussEnvelopeThicknessProperties;
  private readonly boundingTrussEnvelopes: ReadonlyRecord<string, TrussEnvelopeData>;

  constructor(
    definingSegment: LineSegment2D,
    thicknessProperties: TrussEnvelopeThicknessProperties,
    boundingTrussEnvelopes: ReadonlyRecord<string, TrussEnvelopeData>,
  ) {
    this.definingSegment = definingSegment;
    this.thicknessProperties = thicknessProperties;
    this.boundingTrussEnvelopes = boundingTrussEnvelopes;
  }

  calculateBevelAndAdjustedPoint(
    point: Point2D,
    options: {
      bevelCut: UserSpecifiedBevelCut | undefined;
      otherTip: Point2D;
      bevelCutTypePreference: BevelCutTypePreference;
      overhang: number;
    },
  ): [Point2D | null, TrussEnvelopeBevelCut | null] {
    if (options.bevelCut === null) return [null, null];
    if (!distanceEquals(options.overhang, 0)) return [null, null];

    const frontSegment = getEnvelopeSegmentFromDefiningSegment(this.definingSegment, this.thicknessProperties, 'Front');
    const centerSegment = getEnvelopeSegmentFromDefiningSegment(
      this.definingSegment,
      this.thicknessProperties,
      'Center',
    );
    const backSegment = getEnvelopeSegmentFromDefiningSegment(this.definingSegment, this.thicknessProperties, 'Back');

    const allTrussEnvelopeEdges = Object.values(this.boundingTrussEnvelopes).flatMap(({ shape }) => shape.edges);
    const segmentsThatCutOffEnvelope = allTrussEnvelopeEdges.filter((trussEnvelopeEdge) =>
      [frontSegment, centerSegment, backSegment].some((envelopeSegment) => {
        const intersection = trussEnvelopeEdge.intersectWithLine(envelopeSegment.toLine());
        return (
          intersection &&
          trussEnvelopeEdge.containsOnInside(intersection) &&
          intersection
            .projectOntoLine(this.definingSegment.toLine())
            .equals(point.projectOntoLine(this.definingSegment.toLine()))
        );
      }),
    );

    const getIntersectionForSegment = (envelopeSegment: LineSegment2D) =>
      minBy(
        segmentsThatCutOffEnvelope
          .map((boundingSegment) => boundingSegment.toLine().intersectWithLine(envelopeSegment.toLine()))
          .filter(notNullish),
        (intersection) => intersection.distanceTo(options.otherTip),
      );

    const frontSegmentIntersection = getIntersectionForSegment(frontSegment);
    const centerSegmentIntersection = getIntersectionForSegment(centerSegment);
    const backSegmentIntersection = getIntersectionForSegment(backSegment);

    const furthestPointAndBevelCutType = maxBy(
      (
        [
          [frontSegmentIntersection, 'Back'],
          [centerSegmentIntersection, 'Double'],
          [backSegmentIntersection, 'Front'],
        ] as const
      )
        .map(([point, bevelCutType]) => (point ? { point, bevelCutType } : null))
        .filter(notNullish)
        .filter(({ bevelCutType }) =>
          options.bevelCut === undefined
            ? doesBevelCutTypeRespectPreference(options.bevelCutTypePreference, bevelCutType)
            : options.bevelCut?.type === bevelCutType,
        ),
      ({ point }) => point.projectOntoLine(this.definingSegment.toLine()).distanceTo(this.definingSegment.midpoint),
    );

    if (!furthestPointAndBevelCutType) return [null, null];
    const { point: furthestPoint, bevelCutType } = furthestPointAndBevelCutType;

    const bevelCutAngle =
      options.bevelCut?.angle ??
      (() => {
        // coordinate space:
        // x: direction normal to defining segment (increasing from Back, x=0)
        // y: direction parallel to defining segment (positive means further from midpoint)
        const pointsWithCoordinates = [
          { point: frontSegmentIntersection, xCoord: this.thicknessProperties.thickness },
          { point: centerSegmentIntersection, xCoord: this.thicknessProperties.thickness / 2 },
          { point: backSegmentIntersection, xCoord: 0 },
        ]
          .map(({ point, xCoord }) => (point ? { point, xCoord } : null))
          .filter(notNullish)
          .map(({ point, xCoord }) => ({
            point,
            coordinates: new Vector2D(
              xCoord,
              point.projectOntoLine(this.definingSegment.toLine()).distanceTo(this.definingSegment.midpoint),
            ),
          }));

        const furthestPointCoordinates = pointsWithCoordinates.find(({ point }) =>
          point.equals(furthestPoint),
        )!.coordinates;

        return max(
          pointsWithCoordinates
            .filter(({ point }) => !point.equals(furthestPoint))
            .map(({ coordinates }) => furthestPointCoordinates.subtract(coordinates))
            .map(({ x, y }) => convertRadiansToDegrees(Math.atan2(y, Math.abs(x)))),
        );
      })();

    if (bevelCutAngle !== undefined && bevelCutAngle < 0 && !options.bevelCut?.angle)
      console.error('Negative-angle bevel cut encountered during automatic beveling');
    const bevelCut: TrussEnvelopeBevelCut | null =
      bevelCutAngle === undefined || angleLessThanOrEqual(bevelCutAngle, 0)
        ? null
        : {
            type: bevelCutType,
            angle: bevelCutAngle,
          };

    const furthestPointOnDefiningSegment = furthestPoint.projectOntoLine(this.definingSegment.toLine());
    return [furthestPointOnDefiningSegment, bevelCut];
  }
}
